import React, { useMemo } from 'react';
import styled, { withTheme } from 'styled-components/macro';
import PropTypes from 'prop-types';
import { ImageSelect, Text } from 'components/atoms';
import ContentLoader from 'react-content-loader';
import { modifiersType } from 'types';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { useEditorContext, actions } from 'context/EditorContext'

const loadingImages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const GALLERY_MODIFIERS = {
  stretch: () => `
    & > div {
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    }
  `,
};

const IMAGE_MODIFIERS = {
  stretch: () => `
    width: 100%;
    height: auto;
  `,
};

const LOADER_MODIFIERS = {
  stretch: () => `
    width: 100%;
    height: auto;
    min-height: 174.86px;
  `,
};

// handles image listing and image upload

const MediaGallery = ({
  onSelect,
  images,
  isLoading,
  modifiers: styleModifiers,
  isInput,
  search,
}) => {
  const imageHoverMessage = useMemo(
    () => (isInput ? 'Selecionar imagem' : 'Ver imagem'),
    [isInput]
  );
  return (
    <>
      {!isLoading && images && images.length === 0
        ? (
          <Placeholder>
            {search === ''
              ? (
                <Text modifiers={['light']}>
                Sem resultados
                </Text>
              )
              : (
                <Text modifiers={['light']}>
              Sem resultados para a pesquisa:
                  {' '}
              &quot;
                  {search}
              &quot;
                </Text>
              )}
          </Placeholder>
        )
        : (
          <Container modifiers={styleModifiers}>
            {images.map(image => {
              const {
                id, url, data, dateToken 
              } = image;
              const urlObj = new URL(url);
              urlObj.searchParams.set('dateToken', dateToken);
              const updateUrl = urlObj.toString();
              return (
                <ImageWrapper
                  key={id}
                  onClick={() => onSelect(image)}
                  modifiers={styleModifiers}
                >
                  <ImageSelect
                    src={updateUrl}
                    alt={data.length > 0 ? data[0].alt : ''}
                    message={imageHoverMessage}
                  />
                </ImageWrapper>
              );
            })}
            {isLoading && loadingImages.map((index) => (
              <ImageLoader key={index} modifiers={styleModifiers} />
            ))}
          </Container>
        )
      }
    </>
  );
};

const Top = styled.div`
  width: 100%;
  padding-bottom: ${props => props.theme.spacing.l};
  padding-right: ${props => props.theme.spacing.xl2};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

Top.Start = styled.div`
  display: inline-flex;
  button {
    width: auto;
    padding-right: ${props => props.theme.spacing.m};
  }
`;

Top.End = styled.div``;

const width = '227.33px'
const ImageWrapper = styled.div`
  width: ${width};
  height: calc(${width} * 9 / 16);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.greyLighter};
  cursor: pointer;

  img {
    width: 100%;
    height: auto;
  }
  ${applyStyleModifiers(IMAGE_MODIFIERS)};
`;

const Container = styled.div`
  overflow: auto;
  padding-right: ${props => props.theme.spacing.xl2};
  min-height: 336px;
  display: grid;
  min-height: calc(178px * 9 / 16);
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: ${props => props.theme.spacing.xs};
  grid-row-gap: ${props => props.theme.spacing.xs};
  grid-auto-rows: auto;

  /* width */
  ::-webkit-scrollbar {
    width: 5px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.bg};
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.bgDarker};
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.colors.bgDarker};
  }
  ${applyStyleModifiers(GALLERY_MODIFIERS)};
`;

const ImageLoader = withTheme(({ theme, modifiers }) => (
  <LoaderWrapper modifiers={modifiers}>
    <ContentLoader
      speed={1}
      primaryColor={theme.colors.bgLighterS}
      secondaryColor={theme.colors.bgLighter}
    />
  </LoaderWrapper>
));

const LoaderWrapper = styled.div`
  width: ${width};
  height: calc(${width} * 9 / 16);
  svg {
    width: 100%;
    height: 100%;
  }
  ${applyStyleModifiers(LOADER_MODIFIERS)};
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 10rem;
`;

MediaGallery.propTypes = {
  onSelect: PropTypes.func,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      resizer: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  modifiers: modifiersType,
  isInput: PropTypes.bool,
  search: PropTypes.string,
};

MediaGallery.defaultProps = {
  onSelect: () => null,
  images: [],
  isLoading: false,
  modifiers: [],
  isInput: false,
  search: ''
};

export default MediaGallery;
